import { useState } from "react"
import { useDispatch } from "react-redux"
import { startRefetchSelectedRoom } from "src/pages/home/store/thunks/rooms.thunk"
import { useRoom } from "src/pages/home/room-detail/hooks"
import {
    anyOf,
    assignedTo,
    withCleaningType,
    withoutPendingTasks,
    allOf,
    useWaitForRoom,
} from "src/pages/home/room-detail/hooks/useWaitForRoom"
import DrawerWrapper from "src/pages/home/room-detail/sections/DrawerWrapper"
import { HomeView } from "src/pages/home/room-detail/sections/views/Views"
import DrawerSkeleton from "src/pages/home/room-detail/sections/DrawerSkeleton/DrawerSkeleton"
import { Block, Tabs } from "src/pages/home/room-detail/sections/elements/Elements"
import ReportIncidence from "src/pages/home/room-detail/Modals/ReportIncidence/ReportIncidence"
import CleanStaff from "../../general/sections/CleanStaff"
import TipoLimpieza from "../../general/sections/TipoLimpieza"
import ActiveCleanStaff from "../../ocupada/sections/ActiveCleanStaff/ActiveCleanStaff"
import SwapStaff from "../../limpieza/sections/CleanStaff"
import ChangeCleanType from "../../general/sections/ChangeCleanType/ChangeCleanType"
import Payments from "../../ocupada/sections/tabs/payments/Payments"
import Comments from "../../ocupada/sections/tabs/comments/Comments"
import TiempoRestante from "./TiempoRestante/TiempoRestante"
import Huesped from "./tabs/huesped/Huesped"
import { useProfile } from "src/shared/hooks/useProfile"
import useIsColaboradorActive from "src/shared/hooks/useIsColaboradorActive"
import { RoleNames } from "src/shared/hooks/useAuth"
import { Estados_Habitaciones } from "src/gql/schema"
import AvatarProgressCard from "src/shared/components/data-display/AvatarProgressCard/AvatarProgressCard"
import { addTimeByCleaningType } from "src/shared/helpers/addTimeByCleaningType"
import { useDate } from "src/shared/hooks/useDate"
import { useCurrentDate } from "src/shared/providers/CurrentdateProvider"

import "./Home.css"

type tabsPaths =
    | "huesped"
    | "payments"
    | "comments"
    | "cleanRoom"
    | "tipoLimpieza"
    | "active-cleaning-staff"
    | "swap-colaborador"
    | "cambio-tipo-limpieza"

const homeTabsList: { label: string; path: tabsPaths; number: number }[] = [
    { label: "Huésped", path: "huesped", number: 0 },
    { label: "Pagos", path: "payments", number: 0 },
    { label: "Comentarios", path: "comments", number: 0 },
]

const Home = () => {
    const room = useRoom()
    const { rolName } = useProfile()
    const { UTCStringToLocalDate } = useDate()
    const [now] = useCurrentDate()
    const dispatch = useDispatch()
    const { InactiveModal, validateIsColabActive } = useIsColaboradorActive()
    const [type, setType] = useState<tabsPaths>("huesped")
    const [isReportIncidenceModalOpen, setIsReportIncidenceModalOpen] = useState(false)
    // Las acciones sobre la habitación regresan al detalle, que muestra el skeleton hasta que llega el cambio
    const { isWaitingForRoom, waitForRoom, waitWhile, stopWaiting, onActionFinished } =
        useWaitForRoom(() => setType("huesped"))
    const [camaristaSelected, setCamaristaSelected] = useState<{ nombre: string; colaborador_id: string }[]>([
        { nombre: "", colaborador_id: "" },
    ])

    const isMantenimiento = rolName === RoleNames.mantenimiento
    const isCleanFlow =
        type === "cleanRoom" ||
        type === "tipoLimpieza" ||
        type === "active-cleaning-staff" ||
        type === "swap-colaborador" ||
        type === "cambio-tipo-limpieza"
    const tareaLimpieza = room?.colaborador_tareas_sin_finalizar?.[0]
    // Mismo criterio que TiempoRestante: se excedió la fecha_condensada de la renta sin hacer check in
    const fechaFin = room?.ultima_renta?.fecha_condensada
    const isNoShow = !!fechaFin && now > UTCStringToLocalDate(fechaFin)

    const visibleTabs = homeTabsList.filter((tab) => !isMantenimiento || tab.path !== "payments")

    return (
        <DrawerWrapper
            withMenu={!isMantenimiento && !isCleanFlow && !isWaitingForRoom && !isNoShow}
            itemsMenu={
                isMantenimiento
                    ? undefined
                    : [
                        {
                            label: "Reportar incidencia",
                            onClick: validateIsColabActive(() => setIsReportIncidenceModalOpen(true)),
                        },
                    ]
            }
            withBackButton={isCleanFlow}
            onBack={() => setType(type === "tipoLimpieza" ? "cleanRoom" : "huesped")}
        >
            {type === "active-cleaning-staff" ? (
                <ActiveCleanStaff
                    onSwapColaborador={() => setType("swap-colaborador")}
                    onStartFinalizar={() => waitForRoom(withoutPendingTasks)}
                    onConfirmFinalizar={onActionFinished}
                />
            ) : type === "swap-colaborador" ? (
                <SwapStaff
                    onNavigate={() => setType("huesped")}
                    onStart={(colaboradorIds) => waitForRoom(
                        // Si la finalizaron mientras tanto ya no hay cambio que esperar
                        anyOf(withoutPendingTasks, assignedTo(colaboradorIds))
                    )}
                    onConfirm={onActionFinished}
                />
            ) : type === "cambio-tipo-limpieza" ? (
                <ChangeCleanType
                    onStart={(tipoLimpieza) => waitForRoom(withCleaningType(tipoLimpieza))}
                    onConfirm={onActionFinished}
                />
            ) : type === "cleanRoom" ? (
                <CleanStaff
                    onNavigate={() => 1}
                    onConfirmItem={({ state, colaboradores }) => {
                        setType(state as tabsPaths)
                        setCamaristaSelected(colaboradores)
                    }}
                />
            ) : type === "tipoLimpieza" ? (
                <TipoLimpieza
                    colaboradorNombre={camaristaSelected?.[0].nombre}
                    colaborador_ids={camaristaSelected?.map((c) => c.colaborador_id)}
                    estadoHabitacion={Estados_Habitaciones.VentaEnLinea}
                    onStart={(tipo) =>
                        waitForRoom(
                            allOf(assignedTo(camaristaSelected.map((c) => c.colaborador_id)), withCleaningType(tipo))
                        )
                    }
                    onConfirm={onActionFinished}
                />
            ) : isWaitingForRoom ? (
                <DrawerSkeleton />
            ) : (
                <HomeView
                    title={`${room?.tipo_habitacion?.nombre} ${room?.numero_habitacion}`}
                    subtitle={
                        isNoShow ? `No show - ER-${room?.ultima_renta?.folio ?? ""}` : "Reservada por venta en línea"
                    }
                    subtitleStyle={{ color: isNoShow ? "var(--Ocupado, #EB5757)" : "" }}
                >
                    <div className="room-detail--venta-en-linea__container">
                        <Block className="room-detail--venta-en-linea__block">
                            {/* Solo un timer: la limpieza activa tiene prioridad sobre el tiempo restante */}
                            {tareaLimpieza ? (
                                <AvatarProgressCard
                                    avatarSrcs={room?.colaborador_tareas_sin_finalizar?.map(
                                        (t) => t?.colaborador?.foto
                                    )}
                                    onClickDescriptionLink={() => setType("active-cleaning-staff")}
                                    avatarName={`${tareaLimpieza?.colaborador?.nombre} ${tareaLimpieza?.colaborador?.apellido_paterno} ${tareaLimpieza?.colaborador?.apellido_materno}`}
                                    timeLimit={UTCStringToLocalDate(
                                        addTimeByCleaningType({
                                            date: tareaLimpieza?.fecha_inicio,
                                            cleaningType: tareaLimpieza?.tipo_limpieza,
                                            tiempoLimpiezaDetallada: room?.tipo_habitacion?.minutos_limpieza_detallada,
                                            tiempoLimpiezaNormal: room?.tipo_habitacion?.minutos_limpieza_normal,
                                            tiempoLimpiezaRetoque: room?.tipo_habitacion?.minutos_limpieza_retoque,
                                        })
                                    )}
                                    timeStart={UTCStringToLocalDate(tareaLimpieza?.fecha_inicio)}
                                />
                            ) : (
                                <TiempoRestante
                                    dateStatusChanged={room?.ultima_renta?.fecha_registro}
                                    dateEnd={room?.ultima_renta?.fecha_condensada}
                                />
                            )}
                            <Tabs
                                value={type}
                                onChange={(value) => setType(value as tabsPaths)}
                                tabList={visibleTabs}
                                className="room-detail--venta-en-linea__tabs"
                            />
                            {type === "huesped" ? (
                                <Huesped
                                    isNoShow={isNoShow}
                                    onClean={() => setType("cleanRoom")}
                                    onSwapColaborador={() => setType("swap-colaborador")}
                                    onSwapTipoLimpieza={() => setType("cambio-tipo-limpieza")}
                                    onStartFinalizarLimpieza={() => waitForRoom(withoutPendingTasks)}
                                    onConfirmFinalizarLimpieza={onActionFinished}
                                />
                            ) : type === "payments" ? (
                                <Payments />
                            ) : (
                                <Comments readOnly={isNoShow} />
                            )}
                        </Block>
                    </div>
                </HomeView>
            )}
            <ReportIncidence
                isOpen={isReportIncidenceModalOpen}
                onClose={() => setIsReportIncidenceModalOpen(false)}
                onStart={() => {
                    setIsReportIncidenceModalOpen(false)
                    // Crear una incidencia no siempre cambia la habitación, así que no se espera a la suscripción:
                    // sin condición, se espera hasta que onConfirm refresque la habitación o apague la carga
                    waitForRoom()
                }}
                onConfirm={(success) =>
                    success
                        ? waitWhile(() => dispatch(startRefetchSelectedRoom(room?.habitacion_id)))
                        : stopWaiting()
                }
            />
            {InactiveModal}
        </DrawerWrapper>
    )
}

export default Home
